import { FC, useState } from 'react'

import Image from 'next/image'

import { arrowForward, plusIcon } from '@ui/assets'
import { useSignUpHref } from '@ui/layout/signUpHref'

interface Faq {
  question: string
  answer: string
}

export const faqsData: Faq[] = [
  {
    question: 'How much can I request at a time?',
    answer: 'Up to $100 depending on your history and financial habits.',
  },
  {
    question: 'What determines my eligibility and advance amount?',
    answer:
      'Eligibility and the advance amount are determined by a computer analysis of your primary linked bank account, using up to two years of historical income and expense data, along with other criteria. Your account must have a minimum of three consistent current direct deposits from the same source, a positive balance, and must meet other specific requirements.',
  },
  {
    question: 'Does Instant Cash impact my credit score or consider it?',
    answer:
      'No, ATM Instant Cash™ does not impact your credit score and does not consider it. There is no credit check involved, and ATM.com does not report to credit bureaus.',
  },
  {
    question: 'When do I repay my advance?',
    answer:
      'You repay your advance on the date of your next regularly scheduled direct deposit into your linked bank account. The repayment is automatically deducted or you can pay it back early without penalty.',
  },
  {
    question: 'Do I need to be a full-time employee?',
    answer:
      'No, there is no requirement to be a full-time employee. However, you must have a bank account with consistent direct deposits from the same source to be eligible.',
  },
  {
    question: 'How long does it take to receive my advance?',
    answer:
      'You can typically receive your advance within seconds after the request is approved, regardless of business hours or holidays.',
  },
  {
    question: 'How long does it take to get approved?',
    answer:
      'Approval time is variable and determined by the time it takes to analyze your account and ensure eligibility criteria are met.',
  },
  {
    question: "What if I'm not eligible?",
    answer:
      'If you are not eligible, your application will be declined, but you may re-apply after 30 days from the date of the decline notice.',
  },
  {
    question: 'How do you help break debt cycles?',
    answer:
      'ATM Instant Cash™ helps break debt cycles by offering non-recourse cash advances that do not involve high-interest loans or credit checks. This allows you to manage emergency expenses and avoid overdraft fees without incurring additional debt.',
  },
  {
    question: 'Are there any fees?',
    answer:
      "Yes, there are fees associated with ATM Instant Cash™. The Transaction Fee varies depending on the amount of the advance, ranging from $5 to 5% of the advance amount. Additionally, there is up to a $5.99 monthly Platform Fee for access to ATM.com's Personal Finance tools, which includes ATM Instant Cash™.",
  },
]

export const FAQLodge: FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)
  const signUpHref = useSignUpHref()

  return (
    <section id="faq" className="bg-[#e6f7ff] py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start gap-6 lg:flex-row">
          {/* Left column */}
          <div className="flex w-full flex-col gap-10 lg:w-[456px]">
            <h2 className="font-display text-4xl leading-none tracking-[-1.28px] text-[#0a2d4a] md:text-5xl lg:text-[52px]">
              Frequently Asked <span className="text-[#29bfed]">Questions</span>
            </h2>
            <p className="text-[19px] leading-[1.4] text-[#0a2d4a]">
              Have questions about ATM Instant Cash? Find answers to common
              questions about eligibility, fees, and how our service works.
            </p>
            <a
              href={signUpHref}
              className="hidden w-fit items-center gap-4 rounded-full bg-[#0a3754] py-1 pl-6 pr-1 transition-all hover:bg-[#0a3754]/90 lg:inline-flex"
            >
              <span className="text-[17px] font-bold tracking-[-0.34px] text-white">
                Get Started
              </span>
              <div className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#29bfed]">
                <Image src={arrowForward} width={24} height={24} alt="" />
              </div>
            </a>
          </div>

          {/* Right column - FAQ items */}
          <div className="flex w-full flex-col gap-3 lg:w-[700px]">
            {faqsData.map((faq, i) => (
              <div
                key={`${faq.answer.split(' ').join('-')}`}
                className={`overflow-hidden rounded-[24px] bg-[rgba(218,241,250,0.5)] transition-all ${
                  expandedFaq === i ? 'h-auto' : 'h-[80px]'
                }`}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="flex w-full items-center justify-between px-4 py-4 sm:px-6 sm:py-6"
                >
                  <p className="text-lg font-semibold tracking-[-0.44px] text-[#0a2d4a] sm:text-xl lg:text-[22px]">
                    {faq.question}
                  </p>
                  <div
                    className={`flex-shrink-0 transition-transform ${expandedFaq === i ? 'rotate-45' : ''}`}
                  >
                    <Image
                      src={plusIcon}
                      width={36}
                      height={36}
                      alt={expandedFaq === i ? 'Collapse' : 'Expand'}
                    />
                  </div>
                </button>
                {expandedFaq === i && (
                  <div className="px-6 pb-6">
                    <p className="text-[18px] leading-[1.6] text-[#0a2d4a]/80">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
